import React, { useState, useEffect } from 'react';
import axios from 'axios';

function formatCurrency(value) {
  return `${Number(value || 0).toLocaleString('vi-VN')} ₫`;
}

function paymentStatusLabel(status) {
  if (status === 'paid') {
    return 'Đã thu';
  }

  if (status === 'partial') {
    return 'Thu một phần';
  }

  return 'Chưa thu';
}

function Reports() {
  const [revenueData, setRevenueData] = useState([]);
  const [profitData, setProfitData] = useState([]);
  const [debtData, setDebtData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [revenue, profit, debts] = await Promise.all([
        axios.get('/api/reports/revenue'),
        axios.get('/api/reports/profit'),
        axios.get('/api/reports/debts')
      ]);
      setRevenueData(revenue.data);
      setProfitData(profit.data);
      setDebtData(debts.data);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="loading">Đang tải báo cáo...</div>;
  }

  const totalRevenue = revenueData.reduce((sum, item) => sum + Number(item.total_revenue || 0), 0);
  const totalCollected = revenueData.reduce((sum, item) => sum + Number(item.collected_amount || 0), 0);
  const totalDebt = revenueData.reduce((sum, item) => sum + Number(item.debt_amount || 0), 0);
  const totalProfit = profitData.reduce((sum, item) => sum + Number(item.profit || 0), 0);
  const paidOrders = revenueData.reduce((sum, item) => sum + Number(item.paid_orders || 0), 0);
  const unpaidOrders = revenueData.reduce((sum, item) => sum + Number(item.unpaid_orders || 0), 0);
  const partialOrders = revenueData.reduce((sum, item) => sum + Number(item.partial_orders || 0), 0);

  return (
    <div>
      <div className="stats-grid" style={{ marginBottom: '30px' }}>
        <div className="stat-card">
          <div className="stat-label">Tổng doanh thu</div>
          <div className="stat-value">{formatCurrency(totalRevenue)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Đã thu</div>
          <div className="stat-value">{formatCurrency(totalCollected)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Công nợ</div>
          <div className="stat-value">{formatCurrency(totalDebt)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Tổng lợi nhuận</div>
          <div className="stat-value">{formatCurrency(totalProfit)}</div>
        </div>
      </div>

      <div className="card">
        <h3 className="card-title">Thu tiền và công nợ theo ngày</h3>
        {revenueData.length === 0 ? (
          <div className="empty-state">Chưa có dữ liệu</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Ngày</th>
                <th>Số hóa đơn</th>
                <th>Doanh thu</th>
                <th>Đã thu</th>
                <th>Còn nợ</th>
                <th>Trạng thái</th>
              </tr>
            </thead>
            <tbody>
              {revenueData.map((item, index) => (
                <tr key={index}>
                  <td>{item.date}</td>
                  <td>{item.order_count}</td>
                  <td>{formatCurrency(item.total_revenue)}</td>
                  <td>{formatCurrency(item.collected_amount)}</td>
                  <td>{formatCurrency(item.debt_amount)}</td>
                  <td>
                    <div>Đã thu: {item.paid_orders}</div>
                    <div>Chưa thu: {item.unpaid_orders}</div>
                    <div>Thu một phần: {item.partial_orders}</div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: '30px' }}>
        <h3 className="card-title">Quản lý công nợ</h3>
        <div style={{ marginBottom: '12px', color: '#666' }}>
          Hóa đơn đã thu: <strong>{paidOrders}</strong> • Chưa thu: <strong>{unpaidOrders}</strong> • Thu một phần: <strong>{partialOrders}</strong>
        </div>
        {debtData.length === 0 ? (
          <div className="empty-state">Hiện không có hóa đơn công nợ chưa thu</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Hóa đơn</th>
                <th>Khách hàng</th>
                <th>Loại</th>
                <th>Tổng tiền</th>
                <th>Đã thu</th>
                <th>Còn nợ</th>
                <th>Trạng thái</th>
                <th>Ngày</th>
              </tr>
            </thead>
            <tbody>
              {debtData.map((item) => (
                <tr key={item.id}>
                  <td>#{item.id}</td>
                  <td>{item.customer_name}</td>
                  <td>{item.order_type === 'wholesale' ? 'Bán sỉ' : 'Bán lẻ'}</td>
                  <td>{formatCurrency(item.total_amount)}</td>
                  <td>{formatCurrency(item.amount_paid)}</td>
                  <td style={{ color: '#dc3545', fontWeight: 600 }}>{formatCurrency(item.debt_amount)}</td>
                  <td>{paymentStatusLabel(item.payment_status)}</td>
                  <td>{new Date(item.created_at).toLocaleDateString('vi-VN')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="card" style={{ marginTop: '30px' }}>
        <h3 className="card-title">Lợi nhuận theo sản phẩm</h3>
        {profitData.length === 0 ? (
          <div className="empty-state">Chưa có dữ liệu</div>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Sản phẩm</th>
                <th>Bán được</th>
                <th>Doanh thu</th>
                <th>Chi phí</th>
                <th>Lợi nhuận</th>
              </tr>
            </thead>
            <tbody>
              {profitData.map((item) => (
                <tr key={item.id}>
                  <td>{item.name}</td>
                  <td>{item.total_sold}</td>
                  <td>{formatCurrency(item.total_revenue)}</td>
                  <td>{formatCurrency(item.total_cost)}</td>
                  <td style={{ color: Number(item.profit) > 0 ? 'green' : 'red' }}>
                    {formatCurrency(item.profit)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export default Reports;
